import React, { useEffect, useRef, useState } from 'react';
import { search as searchApi } from '@/utils/api';
import { useNavigate } from 'react-router-dom';
import { Search as SearchIcon, FolderKanban, User, Bug, BookOpen, FileSignature } from 'lucide-react';

const TYPE_META = {
  project: { icon: FolderKanban, color: '#006080', label: 'Project' },
  client: { icon: User, color: '#5B2D8E', label: 'Client' },
  bug: { icon: Bug, color: '#B34A3A', label: 'Bug' },
  kb: { icon: BookOpen, color: '#3C8A5A', label: 'KB' },
  sow: { icon: FileSignature, color: '#D9A441', label: 'SOW' },
};

export default function SearchPage() {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(() => {
      searchApi.query(q).then(r => setResults(r.data.results || [])).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const grouped = results.reduce((acc, r) => {
    (acc[r.type] = acc[r.type] || []).push(r);
    return acc;
  }, {});

  return (
    <div className="p-6 animate-fade-in max-w-3xl mx-auto" data-testid="search-page">
      <div className="relative mb-6">
        <SearchIcon size={18} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted)' }} />
        <input
          ref={inputRef}
          className="input-agency pl-11 py-3 text-base"
          placeholder="Search projects, clients, bugs, KB, SOWs…"
          value={q}
          onChange={e => setQ(e.target.value)}
          data-testid="search-input"
          autoFocus
        />
      </div>

      {q.length < 2 ? (
        <p className="text-xs text-center" style={{ color: 'var(--muted)' }}>Type at least 2 characters to search</p>
      ) : loading ? (
        <p className="text-xs text-center" style={{ color: 'var(--muted)' }}>Searching…</p>
      ) : results.length === 0 ? (
        <div className="text-center py-10">
          <p className="text-sm" style={{ color: 'var(--muted)' }}>No matches for "{q}"</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="search-results">
          {Object.entries(grouped).map(([type, rows]) => {
            const meta = TYPE_META[type] || { icon: SearchIcon, color: '#5C6B72', label: type };
            const Icon = meta.icon;
            return (
              <div key={type}>
                <p className="text-xs font-bold uppercase mb-2 flex items-center gap-1.5" style={{ color: meta.color }}>
                  <Icon size={11} /> {meta.label} ({rows.length})
                </p>
                <div className="space-y-1.5">
                  {rows.map(r => (
                    <div key={r.id} className="card-interactive p-3" onClick={() => r.url && navigate(r.url)} data-testid={`result-${r.type}-${r.id}`}>
                      <p className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>{r.title}</p>
                      {r.subtitle && <p className="text-xs" style={{ color: 'var(--muted)' }}>{r.subtitle}</p>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
